import { UnresolvedProductMapDetails, ResolvedProductMapDetails } from "@deliverr/replenishment-client";

import { useCallback } from "react";
import { selectTransferCreate } from "transfers/create/store/TransferCreateSelectors";
import { useSelector } from "react-redux";
import { getCsvDownloadUrl } from "common/utils/getCsvDownloadUrl";
import { BULK_PRODUCT_UPLOAD_REPORT_CSV_HEADERS } from "./BulkProductUploadReportCsvHeaders";
import { BulkProductUploadConstants } from "./BulkProductUploadConstants";
import { useIntl } from "react-intl";

const safeString = (val: any) => {
  if (val === undefined || val === null) {
    return "";
  }
  const stringVal = String(val);
  // Wrap in quotes if the string contains comma to ensure proper CSV formatting
  if (stringVal.includes(",")) {
    return `"${stringVal}"`;
  }
  return stringVal;
};

// TO-DO: Handle resolved products with multiple items
const formatResolvedProductRow = (rowNumber: string, resolvedProductDetails: ResolvedProductMapDetails) => {
  const { productRow, resolvedItems } = resolvedProductDetails;
  const resolvedItem = resolvedItems[0];
  return [
    rowNumber,
    BulkProductUploadConstants.SUCCESS_STATUS,
    productRow.msku ?? "",
    resolvedItem?.dsku ?? "",
    safeString(resolvedItem?.unitsPerPack),
    safeString(productRow.numberOfBoxes),
    safeString(resolvedItem?.totalUnits),
    productRow.lotNumber ?? "",
    "",
    "",
  ];
};

const formatUnresolvedProductRow = (rowNumber: string, unresolvedDetails: UnresolvedProductMapDetails) => {
  const { productRow, message, code } = unresolvedDetails;
  return [
    rowNumber,
    BulkProductUploadConstants.ERROR_STATUS,
    productRow.msku ?? "",
    productRow.dsku ?? "",
    safeString(productRow.unitsPerBox),
    safeString(productRow.numberOfBoxes),
    safeString(productRow.totalUnits),
    productRow.lotNumber ?? "",
    safeString(message),
    code ?? "",
  ];
};

export const useBulkProductUploadReport = () => {
  const { formatMessage } = useIntl();
  const { bulkProductUploadResult } = useSelector(selectTransferCreate);

  const generateBulkProductUploadReportLink = useCallback(() => {
    if (!bulkProductUploadResult) {
      return;
    }
    const { resolvedProducts, unresolvedProducts } = bulkProductUploadResult;
    const headers = Object.values(BULK_PRODUCT_UPLOAD_REPORT_CSV_HEADERS).map((header) => formatMessage(header));
    const resolvedRows = Object.entries(resolvedProducts).map(([rowNumber, resolvedProductDetails]) =>
      formatResolvedProductRow(rowNumber, resolvedProductDetails)
    );
    const unresolvedRows = Object.entries(unresolvedProducts).map(([rowNumber, unresolvedDetails]) =>
      formatUnresolvedProductRow(rowNumber, unresolvedDetails)
    );
    const csvData = [...resolvedRows, ...unresolvedRows].sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
    return getCsvDownloadUrl(headers, csvData, BulkProductUploadConstants.SEPARATOR);
  }, [bulkProductUploadResult, formatMessage]);

  return {
    generateBulkProductUploadReportLink,
  };
};
